/**
 * Small shared formatters for the Phase 105 views. No framework deps so
 * they stay trivially testable.
 *
 * History and snippet timestamps are Unix milliseconds (Android
 * System.currentTimeMillis / Date.now). Values below 1e12 are treated as
 * legacy seconds and scaled once for display.
 */

function toMillis(ts: number): number {
  // Seconds are ~1e9; milliseconds are ~1e12. Threshold leaves room for
  // both 2001-era seconds and 2001-era milliseconds.
  return ts < 1e12 ? ts * 1000 : ts;
}

/** Format a Unix-ms (or legacy Unix-seconds) timestamp as a locale string. */
export function formatTimestamp(unixMs: number): string {
  if (!Number.isFinite(unixMs)) return '—';
  return new Date(toMillis(unixMs)).toLocaleString();
}

/** Format a Unix-ms (or legacy Unix-seconds) timestamp as a short date. */
export function formatDate(unixMs: number): string {
  if (!Number.isFinite(unixMs)) return '—';
  return new Date(toMillis(unixMs)).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Format a millisecond duration: "1.2s", "2m 3s", "1h 5m". */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  const sec = ms / 1000;
  if (sec < 60) return `${sec.toFixed(sec < 10 ? 1 : 0)}s`;
  const mins = Math.floor(sec / 60);
  const rem = Math.round(sec % 60);
  if (mins < 60) return `${mins}m ${rem}s`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  return `${hrs}h ${remMins}m`;
}

/** Truncate text to `n` chars with an ellipsis. */
export function truncate(text: string, n: number): string {
  if (text.length <= n) return text;
  return `${text.slice(0, n).trimEnd()}…`;
}

/** Normalize a snippet trigger the same way the Android repo does. */
export function normalizeTrigger(trigger: string): string {
  return trigger.trim().normalize('NFC').toLowerCase();
}
